import type { ReactNode } from 'react'
import { useCoachingStore } from '../store'
import { OverallComment } from './overall-comment'
import { RankingBanner } from './ranking-banner'
import type { ReviewBodyProps } from './review-tab'
import { AlternativeStories } from './sections/alternative-stories'
import { DeliverySection } from './sections/delivery-section'
import { PostMortemSection } from './sections/post-mortem-section'
import { StarAnswer } from './sections/star-answer'
import { StoryFit } from './sections/story-fit'

/**
 * Behavioral review body (spec §8.5, conflict-story seed) — the per-type
 * `behavioral` entry in `review-tab.tsx`'s `BODY_BY_TYPE` map. Owns the
 * shared sections (ranking banner, post-mortem, overall — same components
 * A4 built for `DefaultReviewBody`, A5/A6 reuse for `coding`/`sd`) plus the
 * behavioral-only sections in spec §8.5's order: Answer · STAR breakdown
 * (incl. the Result-gap callout), Delivery, Story Fit, Alternative stories,
 * post-mortem, overall. Note: behavioral has no shared `PacingSection` —
 * its pacing-equivalent, "Delivery", is a distinct component (A1's note on
 * `behavioral.ts`).
 */
export function BehavioralReview(props: ReviewBodyProps): ReactNode {
  const { submission, getSectionRef } = props
  const { commentsFor } = useCoachingStore().review
  const comments = commentsFor(submission.id)

  return (
    <>
      <RankingBanner ranking={submission.ranking} />
      <div className="flex flex-col gap-3.5">
        <StarAnswer
          submissionId={submission.id}
          comments={comments}
          anchorRef={getSectionRef('answer')}
        />
        <DeliverySection
          pacing={submission.pacing}
          comments={comments}
          anchorRef={getSectionRef('delivery')}
        />
        <StoryFit variant={submission.targetCompany ?? 'general'} />
        <AlternativeStories
          submissionId={submission.id}
          comments={comments}
          anchorRef={getSectionRef('alt-stories')}
        />
        <PostMortemSection
          postMortem={submission.postMortem}
          comments={comments}
          anchorRef={getSectionRef('postmortem')}
        />
        <div ref={getSectionRef('overall')}>
          <OverallComment submissionId={submission.id} />
        </div>
      </div>
    </>
  )
}
